import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../services/api'
import { PortalBackButton } from '../components/PortalBackButton'
import { AppOverlay } from '../components/AppOverlay'

interface LaunchData {
  url: string
  mode: string
  overlay_config: {
    show_feedback?: boolean
    feedback_prompt?: string
  } | null
  variant?: string
  application: {
    id: string
    app_id: string
    name: string
  }
}

export function AppWrapper() {
  const { accessId } = useParams<{ accessId: string }>()
  const [launchData, setLaunchData] = useState<LaunchData | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch launch data and create session
  useEffect(() => {
    const launch = async () => {
      try {
        const data = await api.get<LaunchData>(`/apps/${accessId}/launch`)
        setLaunchData(data)

        // Create session
        const session = await api.post<{ id: string }>('/sessions', {
          application_id: data.application.id,
          variant: data.variant,
          mode: data.mode,
        })
        setSessionId(session.id)
      } catch (err: any) {
        setError(err.message || 'Failed to launch app')
      } finally {
        setLoading(false)
      }
    }

    if (accessId) launch()
  }, [accessId])

  // End session on unmount
  useEffect(() => {
    return () => {
      if (sessionId) {
        api.patch(`/sessions/${sessionId}`, {}).catch(() => {})
      }
    }
  }, [sessionId])

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-[var(--color-text-secondary)]">Loading application...</div>
      </div>
    )
  }

  if (error || !launchData) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-[var(--color-bg)]">
        <div className="card max-w-md text-center">
          <p className="text-red-400 mb-4">{error || 'Failed to load application'}</p>
          <button
            onClick={() => window.history.back()}
            className="btn btn-secondary"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-[var(--color-bg)]">
      {/* Portal back button — always visible */}
      <PortalBackButton />

      {/* App iframe */}
      <iframe
        src={launchData.url}
        className="w-full h-full border-0"
        title={launchData.application.name}
        allow="clipboard-write; fullscreen"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals"
      />

      {/* Feedback overlay */}
      {sessionId && (
        <AppOverlay
          appId={launchData.application.id}
          sessionId={sessionId}
          overlayConfig={launchData.overlay_config}
        />
      )}
    </div>
  )
}
